import { ApiError, handleApiError } from '@/handleApiError'
import api from '@/modules/product/api'
import useProduct from '@/modules/product/useProduct'
import useProductSearch from '@/modules/product/useProductSearch'
import useStartPage from '@/modules/startPage/useStartPage'
import useTopi from '@/modules/topi/useTopi'
import {
  BatchProductPriceUpdate,
  BatchRuntimeRequest,
  ProductPriceUpdate,
  RuntimeRequest
} from '@/types/index'
import { defineStore } from 'pinia'

const cacheDuration = 5 * 60 * 1000 // 5 minutes

interface SingleProductPriceUpdateCacheEntry {
  lastUpdate: Date
  data: ProductPriceUpdate
}
interface BatchProductPriceUpdateCacheEntry {
  lastUpdate: Date
  data: BatchProductPriceUpdate | undefined
}

interface BatchProductPriceUpdateCacheCheckResult {
  cachedBatchProductPriceUpdates: BatchProductPriceUpdate[]
  uncachedProductIds: number[]
}
interface RealtimePricesState {
  coladaLoading: Set<number>
  loading: number[]
  cachedSingleUpdates: Record<number, SingleProductPriceUpdateCacheEntry>
  cachedBatchUpdates: Record<number, BatchProductPriceUpdateCacheEntry>
}

const initialState = (): RealtimePricesState => {
  return {
    coladaLoading: new Set<number>(),
    loading: [],
    cachedSingleUpdates: {},
    cachedBatchUpdates: {}
  }
}

const useRealtimePrices = defineStore('realtimePrices', {
  state: () => initialState(),
  actions: {
    addColadaLoading(ids: Set<number>) {
      ids.forEach((id) => this.coladaLoading.add(id))
    },
    removeColadaLoading(ids: Set<number>) {
      ids.forEach((id) => this.coladaLoading.delete(id))
    },
    async loadRealtimePrices(request: RuntimeRequest, forceRefresh = false) {
      const cachedUpdate = getCachedProductPriceUpdate(request.productId, this)
      if (cachedUpdate && !forceRefresh) {
        useProduct().updateProductPrices(cachedUpdate)
        return Promise.resolve()
      }
      this.loading = [request.productId]

      return api
        .loadRealtimePrices(request)
        .then(({ data }) => {
          useProduct().updateProductPrices(data)
          updateCachedSingleUpdate(data)
        })
        .catch((error: ApiError) => {
          handleApiError(error, {
            appearance: 'NONE'
          })
        })
        .finally(() => {
          this.loading = []
        })
    },
    async loadRealtimePricesBatch(request: BatchRuntimeRequest, forceRefresh = false) {
      let cachedBatchProductUpdates: BatchProductPriceUpdate[] = []
      if (!forceRefresh) {
        const cachedBatchEntries = getBatchProductPriceUpdatesFromCache(request.productIds)
        if (cachedBatchEntries.uncachedProductIds.length === 0) {
          cachedBatchEntries.cachedBatchProductPriceUpdates.forEach((productPriceUpdate) => {
            useProductSearch().updateProduct(productPriceUpdate)
          })
          return Promise.resolve()
        }
        request.productIds = cachedBatchEntries.uncachedProductIds
        cachedBatchProductUpdates = cachedBatchEntries.cachedBatchProductPriceUpdates
      }
      this.loading = request.productIds

      return api
        .loadRealtimePricesBatch({ ...request, productIds: request.productIds })
        .then(({ data }) => {
          data.concat(cachedBatchProductUpdates).forEach((productPriceUpdate) => {
            useProductSearch().updateProduct(productPriceUpdate)
          })
          updateCachedBatchUpdate(data)
          useTopi().setCartItems(useProductSearch().items)
        })
        .catch((error: ApiError) => {
          handleApiError(error, {
            appearance: 'NONE'
          })
        })
        .finally(() => {
          this.loading = []
        })
    },
    async loadRealtimePricesBatchForStartPage(request: BatchRuntimeRequest, forceRefresh = false) {
      let cachedBatchProductUpdates: BatchProductPriceUpdate[] = []
      if (!forceRefresh) {
        const cachedBatchEntries = getBatchProductPriceUpdatesFromCache(request.productIds)
        if (cachedBatchEntries.uncachedProductIds.length === 0) {
          cachedBatchEntries.cachedBatchProductPriceUpdates.forEach((productPriceUpdate) => {
            useStartPage().updateProduct(productPriceUpdate)
          })
          return Promise.resolve()
        }
        request.productIds = cachedBatchEntries.uncachedProductIds
        cachedBatchProductUpdates = cachedBatchEntries.cachedBatchProductPriceUpdates
      }
      this.loading = request.productIds

      return api
        .loadRealtimePricesBatch({ ...request, productIds: request.productIds })
        .then(({ data }) => {
          data.concat(cachedBatchProductUpdates).forEach((productPriceUpdate) => {
            useStartPage().updateProduct(productPriceUpdate)
          })
          updateCachedBatchUpdate(data)
          useTopi().setCartItems(useStartPage().startPage.featuredProducts)
        })
        .catch((error: ApiError) => {
          handleApiError(error, {
            appearance: 'NONE'
          })
        })
        .finally(() => {
          this.loading = []
        })
    },
    async loadRealtimePricesForSearch(request: RuntimeRequest, forceRefresh = false) {
      const cachedUpdate = getCachedProductPriceUpdate(request.productId, this)
      if (cachedUpdate && !forceRefresh) {
        useProductSearch().updateProduct(cachedUpdate)
        return Promise.resolve()
      }
      this.loading = [request.productId]

      return api
        .loadRealtimePrices(request)
        .then(({ data }) => {
          useProductSearch().updateProduct(data)
          updateCachedSingleUpdate(data)
          useTopi().setCartItems(useProductSearch().items)
        })
        .catch((error: ApiError) => {
          handleApiError(error, {
            appearance: 'NONE'
          })
        })
        .finally(() => {
          this.loading = []
        })
    }
  }
})

const updateCachedSingleUpdate = (productPriceUpdate: ProductPriceUpdate) => {
  const now = new Date()
  const realtimePricesStore = useRealtimePrices()
  const productId = productPriceUpdate.productId
  realtimePricesStore.cachedSingleUpdates[productId] = {
    lastUpdate: now,
    data: productPriceUpdate
  }
}

const updateCachedBatchUpdate = (batchProductPriceUpdates: BatchProductPriceUpdate[]) => {
  const now = new Date()
  const realtimePricesStore = useRealtimePrices()
  batchProductPriceUpdates.forEach((batchProductPriceUpdate) => {
    const productId = batchProductPriceUpdate.productId
    realtimePricesStore.cachedBatchUpdates[productId] = {
      lastUpdate: now,
      data: batchProductPriceUpdate
    }
  })
}

const getBatchProductPriceUpdatesFromCache = (
  productIds: number[]
): BatchProductPriceUpdateCacheCheckResult => {
  const realtimePricesStore = useRealtimePrices()

  const cachedBatchProductPriceUpdates: BatchProductPriceUpdate[] = []
  const uncachedProductIds: number[] = []

  productIds.forEach((productId) => {
    const cachedUpdate = getCachedBatchProductPriceUpdate(productId, realtimePricesStore)
    if (cachedUpdate) {
      cachedBatchProductPriceUpdates.push(cachedUpdate)
    } else {
      uncachedProductIds.push(productId)
    }
  })

  return {
    cachedBatchProductPriceUpdates,
    uncachedProductIds
  }
}

const getCachedProductPriceUpdate = (
  productId: number,
  realtimePricesStore: RealtimePricesState
): ProductPriceUpdate | undefined => {
  const currentTime = new Date()
  const lastUpdate = realtimePricesStore.cachedSingleUpdates[productId]
  if (!lastUpdate || !lastUpdate.lastUpdate) {
    return undefined
  }
  const { lastUpdate: singleLastUpdate, data } = lastUpdate
  return currentTime.getTime() - singleLastUpdate.getTime() <= cacheDuration ? data : undefined
}

const getCachedBatchProductPriceUpdate = (
  productId: number,
  realtimePricesStore: RealtimePricesState
): BatchProductPriceUpdate | undefined => {
  const currentTime = new Date()
  const lastUpdate = realtimePricesStore.cachedBatchUpdates[productId]
  if (!lastUpdate || !lastUpdate.lastUpdate) {
    return undefined
  }
  const { lastUpdate: batchLastUpdate, data } = lastUpdate
  return currentTime.getTime() - batchLastUpdate.getTime() <= cacheDuration ? data : undefined
}

export default useRealtimePrices
